import { useEffect, useRef, useState } from 'react';
import { DebugLog, setIdentityProvider } from './DebugLog.js';
import { useAppStore } from '../store/useAppStore.js';
import { CloseIcon } from '../icons.js';

/**
 * Hidden by default. Toggle with Shift+D from anywhere outside an input.
 * No floating UI button — keeps the chrome clean for normal users while still
 * letting power users pull up the LiveKit event log when something looks off.
 */
export function DebugPanel() {
  // Force re-render when the signed-in user changes so the header reflects it.
  useAppStore((s) => s.userId);
  const [open, setOpen] = useState(false);
  const [, force] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setIdentityProvider(() => useAppStore.getState().userId); }, []);
  useEffect(() => { const unsub = DebugLog.subscribe(() => force((n) => n + 1)); return () => { unsub(); }; }, []);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.shiftKey && (e.key === 'D' || e.key === 'd') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const t = (document.activeElement as HTMLElement | null)?.tagName;
        if (t !== 'INPUT' && t !== 'TEXTAREA') setOpen((v) => !v);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  });

  if (!open) return null;
  const entries = DebugLog.snapshot();

  async function copy() {
    try {
      await navigator.clipboard.writeText(DebugLog.asText());
    } catch {
      const ta = document.createElement('textarea');
      ta.value = DebugLog.asText();
      ta.style.position = 'fixed'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-[10000] flex flex-col rounded-xl
                 bg-canvas-dark/95 text-ink-200 ring-1 ring-white/10
                 shadow-chrome backdrop-blur-md font-mono"
      style={{ width: 'min(640px, 92vw)', height: 'min(480px, 60vh)' }}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 text-[12px]">
        <span className="text-[0.65rem] uppercase tracking-[0.18em] text-ink-400 font-medium">
          Event log
        </span>
        <span className="text-ink-500">·</span>
        <span className="text-ink-400">{entries.length} entries</span>
        <span className="ml-auto text-[10px] text-ink-500">Shift+D / Esc</span>
        <button
          onClick={copy}
          className="px-2 py-1 rounded-md text-[11px] bg-white/[0.06] hover:bg-white/10 transition"
        >
          Copy
        </button>
        <button
          onClick={() => DebugLog.clear()}
          className="px-2 py-1 rounded-md text-[11px] bg-white/[0.06] hover:bg-white/10 transition"
        >
          Clear
        </button>
        <button
          onClick={() => setOpen(false)}
          aria-label="Close debug panel"
          className="p-1 rounded-md text-ink-400 hover:text-ink-100 hover:bg-white/5 transition"
        >
          <CloseIcon />
        </button>
      </div>
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto scroll-styled px-3 py-1.5 text-[11px] leading-[1.5]"
      >
        {entries.map((e, i) => (
          <div
            key={i}
            className={`whitespace-pre-wrap break-all py-px border-b border-white/5 ${
              e.direction === '→' ? 'text-blue-300'
              : e.direction === '←' ? 'text-emerald-300'
              : e.direction === '⚠' ? 'text-red-300'
              : 'text-ink-400'
            }`}
          >
            {e.time}  [{e.identity}]  {e.direction} {e.event}{e.details ? `  ${e.details}` : ''}
          </div>
        ))}
      </div>
    </div>
  );
}
